import {
  BoundingBox,
  Formatter,
  type RenderContext,
  Stave,
  StaveConnector,
} from "vexflow";
import type { Staff } from "../model/staff";
import type { StaffSystem } from "../model/staff-system";
import {
  getClefNameFromClef,
  getKeySignatureNameFromKeySignature,
  getTimeSignatureStringFromTimeSignature,
  getVexStaveConnectorTypeFromConnectorType,
  getVexVoicesFromMeasure,
} from "../util/model-to-vexflow";
import requireNotNull from "../util/require-not-null";

export interface RenderOptions {
  totalWidth: number;
  totalHeight: number;
  x: number;
  y: number;
  defaultStaveWidth: number;
  defaultSystemGap: number;
  clear: boolean;
  drawConnector: boolean;
}

export function renderStaffSystemAtIndex(
  renderContext: RenderContext,
  staffSystem: StaffSystem,
  index: number,
  options: RenderOptions,
): BoundingBox {
  if (options.clear) {
    renderContext.clear();
  }

  if (staffSystem.staves.length === 0) {
    return new BoundingBox(0, 0, 0, 0);
  }

  if (staffSystem.staves.some((staff) => staff.measures[index] == null)) {
    throw new Error(`Measure does not exist at index ${index}`);
  }

  let boundingBox = null;
  const staves = [];
  let offsetY = options.y;
  for (const staff of staffSystem.staves) {
    const { boundingBox: crtBoundingBox, stave } = renderStaffAtIndex(
      renderContext,
      staff,
      index,
      options.x,
      offsetY,
      options.defaultStaveWidth,
    );
    staves.push(stave);
    offsetY += options.defaultSystemGap + crtBoundingBox.getH();
    if (boundingBox == null) {
      boundingBox = crtBoundingBox;
    } else {
      boundingBox = boundingBox.mergeWith(crtBoundingBox);
    }
  }

  const topStave = requireNotNull(staves.at(0));
  const bottomStave = requireNotNull(staves.at(-1));
  new StaveConnector(topStave, bottomStave)
    .setContext(renderContext)
    .setType("singleLeft")
    .draw();
  new StaveConnector(topStave, bottomStave)
    .setContext(renderContext)
    .setType("singleRight")
    .draw();
  if (options.drawConnector) {
    new StaveConnector(topStave, bottomStave)
      .setContext(renderContext)
      .setType(
        getVexStaveConnectorTypeFromConnectorType(
          staffSystem.metadata.connectorType,
        ),
      )
      .draw();
  }

  return requireNotNull(boundingBox);
}

function renderStaffAtIndex(
  renderContext: RenderContext,
  staff: Staff,
  index: number,
  x: number,
  y: number,
  width: number,
): { boundingBox: BoundingBox; stave: Stave } {
  const measure = requireNotNull(staff.measures[index]);
  const { vexVoices, beams } = getVexVoicesFromMeasure(renderContext, measure);

  const stave = new Stave(x, y, width).setContext(renderContext).draw();

  if (measure.metadata.drawClef) {
    stave.addClef(getClefNameFromClef(measure.clef)).draw();
  }
  if (measure.metadata.drawKeySignature) {
    stave
      .addKeySignature(
        getKeySignatureNameFromKeySignature(measure.keySignature),
      )
      .draw();
  }
  if (measure.metadata.drawTimeSignature) {
    stave
      .addTimeSignature(
        getTimeSignatureStringFromTimeSignature(measure.timeSignature),
      )
      .draw();
  }

  new Formatter()
    .joinVoices(vexVoices)
    .formatToStave(vexVoices, stave, { context: renderContext });

  for (const vexVoice of vexVoices) {
    vexVoice.draw(renderContext, stave);
  }

  for (const beam of beams) {
    beam.draw();
  }

  let boundingBox = stave.getBoundingBox();
  for (const tickable of vexVoices.flatMap((vexVoice) =>
    vexVoice.getTickables(),
  )) {
    boundingBox = boundingBox.mergeWith(
      requireNotNull(tickable.getBoundingBox()),
    );
  }

  return { boundingBox, stave };
}
